'use client';

/**
 * CONTENT SUITE — Content Suite prototype spec: persistent CONTENT OPS rail
 * (11 stations, spec rail styles) + LIBRARY grid (280px spec cards w/ brand
 * hairline, glyph thumb, status pill) + QUEUE publish pipeline with real
 * PUBLISH/RETRY. Data: same /api/content/posts + /api/content/publish as
 * classic. Heavy creative flows (Create/Studio/Revoice/Assistant…) deep-link
 * to classic until the P7 flip — honest pending panels, no mock pipelines.
 */
import { useEffect, useState } from 'react';
import { mono } from '@/future/primitives';

export const TABS = ['LIBRARY', 'VAULT', 'ASSISTANT', 'CREATE', 'STUDIO', 'REVOICE', 'CALENDAR', 'SOCIAL', 'QUEUE', 'ASSETS', 'SETTINGS'] as const;
type Tab = typeof TABS[number];

const SUBS: Record<Tab, string> = {
  LIBRARY: 'EVERY PIECE · ALL BUSINESSES', VAULT: 'LONG-TERM ARCHIVE', ASSISTANT: 'CONTENT COPILOT · SCOPED',
  CREATE: 'BRIEF → RESEARCH → DRAFT', STUDIO: 'VIDEO COMPOSER', REVOICE: 'RE-VOICE ANY VIDEO',
  CALENDAR: 'PUBLISH SCHEDULE', SOCIAL: 'PLATFORM ACCOUNTS', QUEUE: 'PUBLISH PIPELINE',
  ASSETS: 'MEDIA LIBRARY', SETTINGS: 'CONTENT CONFIG',
};
const CLASSIC: Record<Tab, string> = {
  LIBRARY: '/content', VAULT: '/content/vault', ASSISTANT: '/content/assistant', CREATE: '/content/create',
  STUDIO: '/content/studio', REVOICE: '/content/revoice', CALENDAR: '/content/calendar', SOCIAL: '/content/social',
  QUEUE: '/content/queue', ASSETS: '/content/assets', SETTINGS: '/content/settings',
};
const ST_COLOR: Record<string, string> = {
  draft: '#7fa9bd', pending_review: '#ffc36b', approved: '#9ef0ff', posted: '#5df2b4', published: '#5df2b4', failed: '#ff7d8a',
};
const PLATFORM_GLYPH: Record<string, string> = { linkedin: '¶', instagram: '⬡', youtube: '▶', tiktok: '▶', x: '≡' };
const FILTERS = [['ALL', ''], ['DRAFTS', 'draft'], ['PENDING', 'pending_review'], ['PUBLISHED', 'posted']] as const;

export function ContentSuiteSurface() {
  const [tab, setTab] = useState<Tab>('LIBRARY');
  const [posts, setPosts] = useState<any[]>([]);
  const [filter, setFilter] = useState('');
  const [busy, setBusy] = useState<string | null>(null);
  const [loaded, setLoaded] = useState(false);

  const pull = () => fetch('/api/content/posts').then(r => r.json())
    .then(d => { setPosts(Array.isArray(d) ? d : []); setLoaded(true); }).catch(() => setLoaded(true));
  useEffect(() => { pull(); const t = setInterval(pull, 60_000); return () => clearInterval(t); }, []);

  const publish = async (id: string) => {
    setBusy(id);
    try { await fetch('/api/content/publish', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ postId: id }) }); } catch {}
    await pull();
    setBusy(null);
  };

  const counts: Partial<Record<Tab, string>> = {
    LIBRARY: String(posts.length),
    QUEUE: String(posts.filter(p => ['approved', 'pending_review', 'failed'].includes(p.status)).length),
  };
  const filtered = filter ? posts.filter(p => p.status === filter || (filter === 'posted' && p.status === 'published')) : posts;
  const queue = posts.filter(p => p.status !== 'posted' && p.status !== 'published');

  return (
    <div className="relative h-full min-h-0 flex" style={{ gap: 24 }}>
      {/* CONTENT OPS rail — spec verbatim */}
      <div style={{ width: 196, flex: 'none', display: 'flex', flexDirection: 'column', gap: 6 }}>
        <div style={{ ...mono(16, '.3em'), color: '#56788c', padding: '0 12px 8px' }}>CONTENT OPS</div>
        {TABS.map(t => (
          <button key={t} onMouseUp={() => setTab(t)} style={{
            display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', borderRadius: 10,
            ...mono(17, '.2em'), color: tab === t ? '#9ef0ff' : '#56788c', textAlign: 'left',
            border: `1px solid rgba(125,232,255,${tab === t ? '.4' : '0'})`,
            background: tab === t ? 'linear-gradient(170deg,rgba(40,140,180,.12),rgba(8,18,28,.5))' : 'transparent',
            boxShadow: tab === t ? '0 0 24px -8px rgba(0,240,255,.35)' : 'none', transition: 'all .25s',
          }}>
            <span style={{ width: 4, height: 4, borderRadius: '50%', background: tab === t ? '#9ef0ff' : 'rgba(125,232,255,.25)', boxShadow: tab === t ? '0 0 8px rgba(0,240,255,.8)' : 'none', flex: 'none', transition: 'all .25s' }} />
            {t}
            <span style={{ flex: 1 }} />
            <span style={{ ...mono(15), color: tab === t ? '#7de8ff' : '#3d4654' }}>{counts[t] || ''}</span>
          </button>
        ))}
      </div>

      {/* stage */}
      <div className="relative flex-1 min-w-0 flex flex-col">
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, height: 44, flex: 'none' }}>
          <span style={{ ...mono(20, '.3em'), color: '#9ef0ff', textShadow: '0 0 12px rgba(0,240,255,.4)' }}>{tab}</span>
          <span style={{ ...mono(15, '.18em'), color: '#49677a' }}>{SUBS[tab]}</span>
          <span style={{ flex: 1 }} />
          {tab === 'LIBRARY' && FILTERS.map(([label, val]) => (
            <button key={label} onMouseUp={() => setFilter(val)}
              style={filter === val
                ? { padding: '5px 12px', borderRadius: 999, color: '#04121a', background: 'linear-gradient(180deg,#a8eeff,#5fd4ef)', boxShadow: '0 0 14px rgba(0,240,255,.35)', ...mono(16, '.14em') }
                : { padding: '5px 12px', borderRadius: 999, color: '#56788c', border: '1px solid rgba(125,232,255,.18)', ...mono(16, '.14em') }}>
              {label}
            </button>
          ))}
          <a href={CLASSIC[tab]} target="_blank" rel="noreferrer" style={{ padding: '5px 12px', borderRadius: 999, color: '#7fa9bd', border: '1px solid rgba(125,232,255,.22)', ...mono(16, '.14em'), textDecoration: 'none' }}>CLASSIC ↗</a>
        </div>

        {/* LIBRARY — spec 280px cards */}
        {tab === 'LIBRARY' && (
          <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', scrollbarWidth: 'thin', display: 'flex', flexWrap: 'wrap', gap: 16, alignContent: 'flex-start', paddingTop: 16 }}>
            {filtered.map(p => {
              const st = (p.status || 'draft') as string;
              const c = ST_COLOR[st] || '#7fa9bd';
              return (
                <div key={p.id} style={{ width: 280, boxSizing: 'border-box', position: 'relative', borderRadius: 14, padding: 12, background: 'linear-gradient(170deg,rgba(40,140,180,.08),rgba(8,18,28,.55) 60%)', border: '1px solid rgba(125,232,255,.2)', overflow: 'hidden', transition: 'border-color .25s' }}>
                  <div style={{ position: 'absolute', inset: 0, background: 'repeating-linear-gradient(180deg,rgba(140,235,255,.05) 0 1px,transparent 1px 3px)', mixBlendMode: 'screen', opacity: .5, pointerEvents: 'none' }} />
                  <div style={{ position: 'absolute', top: 0, left: '12%', right: '12%', height: 1, background: `linear-gradient(90deg,transparent,${c},transparent)` }} />
                  <div style={{ height: 86, borderRadius: 9, background: 'linear-gradient(160deg,rgba(125,232,255,.14),rgba(6,14,22,.7))', border: '1px solid rgba(125,232,255,.2)', display: 'grid', placeItems: 'center', fontSize: 48, fontWeight: 100, color: c }}>
                    {PLATFORM_GLYPH[p.platform] || '¶'}
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 10 }}>
                    <span style={{ flex: 1, fontWeight: 400, fontSize: 24, color: '#e9f9ff', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{p.hook || p.caption?.slice(0, 60) || 'Untitled'}</span>
                    <span style={{ padding: '2px 8px', borderRadius: 999, border: `1px solid ${c}66`, color: c, ...mono(13, '.14em'), whiteSpace: 'nowrap', flex: 'none' }}>{st.replace('_', ' ').toUpperCase()}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 7, ...mono(14, '.14em'), color: '#56788c' }}>
                    <span>{(p.platform || '').toUpperCase()}</span>
                    <span>{p.created_at ? new Date(p.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : ''}</span>
                  </div>
                </div>
              );
            })}
            {loaded && !filtered.length && <div className="w-full py-16 text-center" style={{ ...mono(17, '.3em'), color: '#56788c' }}>NO PIECES IN THIS FILTER</div>}
            {!loaded && <div className="w-full py-16 text-center" style={{ ...mono(17, '.3em'), color: '#56788c' }}>SYNCING LIBRARY…</div>}
          </div>
        )}

        {/* QUEUE — publish pipeline with real actions */}
        {tab === 'QUEUE' && (
          <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', scrollbarWidth: 'thin', paddingTop: 16 }}>
            {queue.map(p => {
              const st = (p.status || 'draft') as string;
              const c = ST_COLOR[st] || '#7fa9bd';
              return (
                <div key={p.id} style={{ display: 'flex', alignItems: 'center', gap: 16, height: 56, boxSizing: 'border-box', padding: '0 18px', marginTop: 10, borderRadius: 10, background: 'linear-gradient(170deg,rgba(40,140,180,.06),rgba(8,18,28,.42) 60%)', border: '1px solid rgba(125,232,255,.16)' }}>
                  <span style={{ width: 6, height: 6, borderRadius: '50%', background: c, boxShadow: `0 0 8px ${c}`, flex: 'none' }} />
                  <span style={{ flex: 1, fontWeight: 300, fontSize: 24, color: '#d7f2ff', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{p.hook || p.caption?.slice(0, 80) || 'Untitled'}</span>
                  <span style={{ ...mono(15, '.14em'), color: '#56788c' }}>{(p.platform || '').toUpperCase()}</span>
                  <span style={{ padding: '2px 8px', borderRadius: 999, border: `1px solid ${c}66`, color: c, ...mono(13, '.14em'), whiteSpace: 'nowrap' }}>{st.replace('_', ' ').toUpperCase()}</span>
                  {['approved', 'failed'].includes(st) && (
                    <button onMouseUp={() => publish(p.id)} disabled={busy === p.id}
                      style={{ padding: '6px 14px', borderRadius: 999, color: '#04121a', background: 'linear-gradient(180deg,#a8eeff,#5fd4ef)', boxShadow: '0 0 14px rgba(0,240,255,.35)', ...mono(15, '.14em'), opacity: busy === p.id ? .5 : 1 }}>
                      {busy === p.id ? 'PUBLISHING…' : st === 'failed' ? '⟳ RETRY' : '▶ PUBLISH'}
                    </button>
                  )}
                </div>
              );
            })}
            {loaded && !queue.length && <div className="py-16 text-center" style={{ ...mono(17, '.3em'), color: '#56788c' }}>QUEUE EMPTY — EVERYTHING SHIPPED</div>}
          </div>
        )}

        {/* other stations — honest escape hatch until P7 */}
        {tab !== 'LIBRARY' && tab !== 'QUEUE' && (
          <div className="flex-1 grid place-items-center">
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontWeight: 200, fontSize: 44, letterSpacing: '.1em', color: '#e9f9ff' }}>{tab}</div>
              <div style={{ ...mono(16, '.2em'), color: '#56788c', marginTop: 10 }}>{SUBS[tab]}</div>
              <a href={CLASSIC[tab]} target="_blank" rel="noreferrer"
                style={{ display: 'inline-block', marginTop: 18, padding: '9px 20px', borderRadius: 999, color: '#04121a', background: 'linear-gradient(180deg,#a8eeff,#5fd4ef)', boxShadow: '0 0 18px rgba(0,240,255,.4)', ...mono(17, '.18em'), textDecoration: 'none' }}>
                ▶ OPEN IN CLASSIC
              </a>
              <div style={{ ...mono(14, '.16em'), color: '#3d4654', marginTop: 12 }}>FULL {tab} STATION LANDS WITH THE P7 FLIP</div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
